import { useCallback, useEffect, useMemo, useRef } from 'react'

import { LicenseType } from '@ebalance/proto/bfftypes/v1/license_type_pb'
import type { PowerType } from '@ebalance/proto/bffuser/v1/occto_master_model_pb'
import type {
  Control,
  FieldErrors,
  FieldValues,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form'
import { Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  FieldRequiredIndicator,
  FieldRoot,
  HStack,
  Input,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
} from '@enechain/ecloud-designsystem'

import { FieldErrorText, FieldLabel } from '~/components/common/Field'
import { FormSelect } from '~/components/common/FormSelect'
import { HelpIcon } from '~/components/common/HelpIcon'
import {
  type BusinessMasterRow,
  convertListBusinessMastersResponse,
  useListBusinessMasters,
} from '~/hooks/businessMaster/useListBusinessMasters'
import { AREA_CODE_OPTIONS, convertAreaLabel } from '~/models/area'
import {
  type CivilDate,
  civilDateEquals,
  todayCivilDate,
} from '~/models/civilDate'
import { licenseTypeLabel } from '~/models/licenseType'
import { isActiveOn } from '~/models/validPeriod'
import { areaCodeFromBgCode } from '~/pages/AdminOcctoMaster/tabs/BgMaster/models/areaCodeFromBgCode'
import { derivedPowerTypeLabel } from '~/pages/AdminOcctoMaster/tabs/BgMaster/models/labels'
import { availableLicenseTypes } from '~/pages/AdminOcctoMaster/tabs/BgMaster/models/licenseTypeOptions'
import {
  type BgMasterFormInput,
  REQUIRED_BG_CODE_PREFIX,
} from '~/pages/AdminOcctoMaster/tabs/BgMaster/models/schema'
import { ExpiredMasterAlert } from '~/pages/AdminOcctoMaster/tabs/_common/components/ExpiredMasterAlert'
import { ImmutableInput } from '~/pages/AdminOcctoMaster/tabs/_common/components/ImmutableInput'
import { OwnerMembersSection } from '~/pages/AdminOcctoMaster/tabs/_common/components/OwnerMembersSection'
import { TopLevelValidPeriodFields } from '~/pages/AdminOcctoMaster/tabs/_common/components/TopLevelValidPeriodFields'
import { useTodayJst } from '~/pages/AdminOcctoMaster/tabs/_common/hooks/useTodayJst'
import {
  resolveAsOfDate,
  resolveExpiryFallbackLabel,
} from '~/pages/AdminOcctoMaster/tabs/_common/models/validPeriod'

type Props = {
  control: Control<BgMasterFormInput>
  errors: FieldErrors<BgMasterFormInput>
  /** licenseType を監視して発電 BG 固有フィールドの表示を切り替えるために使う。 */
  watch: UseFormWatch<BgMasterFormInput>
  /**
   * 代表事業者 (repBusinessMasterId) を構成事業者の先頭 (members[0]) に同期するため
   * OwnerMembersSection に渡す。
   */
  setValue: UseFormSetValue<BgMasterFormInput>
  /**
   * true の場合、 bgCode を readonly で表示する (edit 時)。
   * AC-006 準拠: 「コード」フィールドのみ登録後に変更不可 (docs/specs/admin/master/
   * occto-master/index.md AC-006「コードフィールドは編集時に読み取り専用」)。
   *
   * licenseType (BG区分) / areaCode (エリア) は design (`design/admin/occto.html`
   * #bgm-license / #bgm-area) では編集時も select のまま選択可能に見えるが、
   * `UpdateBgMasterRequest` (proto/bff/bffuser/v1/occto_master_service.proto) には
   * license_type / area_code フィールドが存在せず、 backend の
   * `BgMasterUsecase.Update` (WithUpdatedMutableFields) もこれらを更新対象に
   * 含めていない (= bgCode の prefix (G/L/D) と licenseType の整合検証があるため、
   * bgCode が readonly な以上 licenseType を独立して変更すると整合が崩れる)。
   * select 化すると「変更したのに保存で silently 無視される」 silent data loss に
   * なるため、
   * 本実装ではこの 2 フィールドも isEdit 時は readonly のまま維持する
   * (= design との既知の意図的差分。 proto/backend 側で licenseType/areaCode の
   * 更新をサポートしない限り select 化しない)。
   */
  isEdit?: boolean
  /**
   * 新規作成モーダルの開閉状態。
   * 開いたタイミング (isOpen が false → true へ遷移) と BG区分 変更時に
   * BGコード 先頭文字を prefill するために使う。
   */
  isOpen: boolean
  /**
   * 今日時点で所属する発電所の電源種別から決まる値 (発電 BG の表示のみ。入力欄にしない: 電源種別の正は
   * 電源ユニット)。未定 (所属が無い・新規登録) は undefined か UNSPECIFIED。
   */
  powerType?: PowerType | undefined
}

/**
 * BG マスタの Create / Edit 共通 form fields コンポーネント。
 *
 * isEdit={true} のとき bgCode / areaCode / licenseType を readonly にする (AC-006 +
 * backend 制約。 詳細は Props.isEdit の JSDoc 参照)。
 * 電源種別は入力欄でなく、 GENERATION の時だけ所属する発電所から決まる値を文で表示する
 * (電源種別の正は電源ユニット)。
 * contractIdNo1 は licenseType === GENERATION のときのみ表示 (contractIdNo2 は
 * BG マスタでは扱わない、 AC-01)。
 * 構成事業者 (members) の編集 UI と代表同期は PlantMaster と共通の
 * OwnerMembersSection に委譲する (= 代表はフラグでなく businessMasterId の
 * 一致で表現する)。
 *
 * field 順序は design (`design/admin/occto.html` #occto-bg-modal) の順に合わせる:
 * BG区分/BGコード → BG名称/エリア → 電源種別/代表事業者 → 契約識別番号1/電源特定コード →
 * 適用開始日/適用終了日 → 備考 → 構成事業者。
 * BG区分を BGコード より先に置くのは design HTML #3732 コメントの意図
 * (区分を先に決めさせて入力誤りを減らす)。
 */
export const BgMasterFormFields = ({
  control,
  errors,
  powerType,
  watch,
  setValue,
  isEdit = false,
  isOpen,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { data: businessMastersData } = useListBusinessMasters()
  // form の毎 render で再評価されないよう memo 化する (= 入力のたびに走る
  // hot path)。 dropdown options 配列の reference が安定するので、 chakra
  // Select の再 reconciliation も最小化される。
  const businessMasters: BusinessMasterRow[] = useMemo(
    () =>
      businessMastersData !== undefined
        ? convertListBusinessMastersResponse(businessMastersData)
        : [],
    [businessMastersData],
  )

  const licenseType = watch('licenseType')
  // AC-OMB-021 (issue #6038): 構成事業者リストの先頭行 (代表事業者) の適用期間は
  // BG 本体の適用期間 (validFrom/validTo) へ同期する。 businessMasterId の代表
  // 同期 (OwnerMembersSection) と同じ流儀だが、 BG 本体の period field 名を
  // OwnerMembersSection (PlantMaster と共有する shared component) に持たせない
  // ため、 BG 固有ロジックとして本 component 側に置く。
  const validFrom = watch('validFrom')
  const validTo = watch('validTo')
  // isEdit のとき areaCode は readonly Input で表示する (select を出さない) ため、
  // 表示用に値を watch する。
  const areaCode = watch('areaCode')
  const isGeneration = licenseType === LicenseType.GENERATION
  const isSelfWheeling = licenseType === LicenseType.SELF_WHEELING

  // AC-OMB-019: BG 区分が「発電」の時、代表・構成事業者 select の選択肢を
  // 発電ライセンスを含む事業者のみに絞り込む。非発電時は既存挙動（全件）。
  const filteredBusinessMasters = useMemo(
    () =>
      isGeneration
        ? businessMasters.filter((bm) =>
            bm.licenseTypes.includes(LicenseType.GENERATION),
          )
        : businessMasters,
    [businessMasters, isGeneration],
  )

  const today = useTodayJst()
  const targetDate = resolveAsOfDate(undefined, validFrom, today)
  const representativeBusinessMasters = useMemo(
    () =>
      filteredBusinessMasters.filter((bm) =>
        isActiveOn(bm.validFrom, bm.validTo, targetDate),
      ),
    [filteredBusinessMasters, targetDate],
  )
  // AC-OMB-023/024 (issue #6315、bg-license-filter-by-business-master 設計書):
  // BG区分 select の選択肢を、 事業者マスタの LicenseCd / Purpose 登録状況で
  // 絞り込む (判定軸・表示順は `~/pages/.../models/licenseTypeOptions` が SoT)。
  // AC-OMB-019 (発電時の代表/構成事業者候補絞り込み) とは別軸のチェックのため、
  // filteredBusinessMasters (発電絞り込み後) ではなく businessMasters (全件) を使う。
  const availableLicenseTypeList = useMemo(
    () => availableLicenseTypes(businessMasters, targetDate),
    [businessMasters, targetDate],
  )
  // ラベルは licenseTypeLabel 経由で i18next に依存する (= 言語切替で変わる) ため
  // memo 化せず render ごとに組み立てる (deps に言語を載せ忘れて stale になるのを防ぐ。
  // native 時代に JSX 内で `.map` していたのと同じ扱い)。
  const licenseTypeOptions = availableLicenseTypeList.map((lt) => ({
    value: String(lt),
    label: licenseTypeLabel(lt),
  }))
  const areaCodeOptions = AREA_CODE_OPTIONS.map((code) => ({
    value: String(code),
    label: convertAreaLabel(code),
  }))
  const businessMasterOptions = useMemo(
    () =>
      representativeBusinessMasters.map((bm) => ({
        value: bm.id,
        label: bm.businessName,
      })),
    [representativeBusinessMasters],
  )
  // 既存レコードで filter (license / as-of) により候補から外れた事業者名も、
  // 編集時に select が空表示にならないよう fallback 表示する。 対象日時点で
  // 終了していれば「（終了）」サフィックス付き、 物理削除済みなら「(削除済み)」
  // を返す (AC-MOI-023、occto-master-fk-as-of-filter 設計書 Decision 2)。 list
  // クエリ取得中は unfiltered に undefined を渡し、 実在参照が一瞬
  // 「(削除済み)」に誤表示されるのを防ぐ (PR #5920 レビュー指摘)。
  const businessMasterFallbackLabel = useCallback(
    (id: string, date: CivilDate) =>
      resolveExpiryFallbackLabel(
        businessMastersData === undefined ? undefined : businessMasters,
        id,
        date,
        {
          idOf: (bm) => bm.id,
          labelOf: (bm) => bm.businessName,
          validTo: (bm) => bm.validTo,
          deletedLabel: t(
            'page:adminOcctoMaster.common.masterFallback.deleted',
          ),
          expiredSuffix: t(
            'page:adminOcctoMaster.common.masterFallback.expiredSuffix',
          ),
          future: {
            validFrom: (bm) => bm.validFrom,
            suffix: t(
              'page:adminOcctoMaster.common.masterFallback.futureSuffix',
            ),
          },
        },
      ),
    [businessMasters, businessMastersData, t],
  )

  const isInitialRender = useRef(true)
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false
      return
    }
    if (!isSelfWheeling) {
      setValue('wheelingLinkCode', undefined)
    }
  }, [isSelfWheeling, setValue])

  // AC-MOI-023: 日付変更による区分の自動変更で選択済み参照を消さない。
  // 未選択の初期値だけを利用可能区分へ補正する (AC-OMB-023/024)。
  useEffect(() => {
    if (isEdit || !isOpen || availableLicenseTypeList.length === 0) {
      return
    }
    const repBusinessMasterId = watch('repBusinessMasterId')
    const members = watch('members') ?? []
    const hasBusinessReference =
      Boolean(repBusinessMasterId) ||
      members.some((member) => member.businessMasterId !== '')
    if (
      !hasBusinessReference &&
      !availableLicenseTypeList.includes(licenseType)
    ) {
      setValue('licenseType', availableLicenseTypeList[0])
    }
  }, [isEdit, isOpen, availableLicenseTypeList, licenseType, setValue, watch])

  // AC-OMB-021: BG 本体の validFrom/validTo が変わるたび、 代表事業者行
  // (members[0]) の適用期間を追随させる (readonly 表示は OwnerMembersSection
  // 側の disabled prop、 保存時の一致検証は backend AC-OMB-022 + schema.ts の
  // superRefine が防御する)。 既に一致していれば setValue を呼ばない
  // (= 初回マウント時の通常ケースで無用な dirty 化を避ける、 businessMasterId
  // 同期 effect と同じ流儀)。 validTo は members フィールド配列のネスト path
  // なので `undefined` 書き込みは RHF に無視される
  // (`PeriodFields`の`clearAsNull` JSDoc 参照) — クリア時は `null` を書く。
  //
  // dep に repValidFrom / repValidTo も含める理由: AC-OMB-019 の license 切替
  // effect (下参照) が `setValue('members', ...)` で members[0] を丸ごと差し替え
  // るケース (無効 rep を emptyMember に置換して validFrom=today / validTo=undefined
  // にする経路) では BG 本体の validFrom/validTo が変わらないため、 BG 側だけを
  // watch していると sync が再走せず代表行の period が BG から drift する。
  // 代表行の period 欄は readonly のためユーザーが自力で直せず AC-OMB-022 の
  // 保存拒否で詰みになる (PR #6047 devin review 指摘)。 civilDateEquals の
  // early-return が「BG と一致していれば setValue しない」を保証するため、
  // rep 側を dep に加えても無限ループにはならない。
  const repValidFrom = watch('members.0.validFrom')
  const repValidTo = watch('members.0.validTo')
  useEffect(() => {
    if (
      civilDateEquals(repValidFrom, validFrom) &&
      civilDateEquals(repValidTo, validTo)
    ) {
      return
    }
    setValue('members.0.validFrom', validFrom, { shouldDirty: true })
    setValue('members.0.validTo', validTo ?? null, { shouldDirty: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validFrom, validTo, repValidFrom, repValidTo])

  // AC-BG13: bgCode が AC-BG02 形式 (5 文字) を満たした時点で、末尾 1 桁に
  // 対応するエリアをエリアフィールドへ自動入力する。手動変更後でも bgCode を
  // 別エリア末尾の正規形式へ再入力するたびに上書きする (design doc §4
  // 「再上書き挙動」、AC-BG13/AC-BG14)。Edit では bgCode / areaCode とも
  // readonly のため発火させない (isEdit プロパティの JSDoc 参照)。
  const bgCode = watch('bgCode')
  useEffect(() => {
    if (isEdit) {
      return
    }
    const derivedAreaCode = areaCodeFromBgCode(bgCode)
    if (derivedAreaCode !== undefined) {
      setValue('areaCode', derivedAreaCode)
    }
  }, [bgCode, isEdit, setValue])

  // issue #3732: BG 区分を BG コードより先に入力させ、かつ区分に応じた
  // 先頭文字を BG コードへ自動セットして入力誤りを減らす。
  // 新規作成・モーダルオープン時、および BG区分 変更時に動作。
  // 既存接頭辞 (G/L/D) があれば置換、それ以外の入力は上書きしない。
  // backend の validateBgMasterCodeLicenseAlignment と同じ対応:
  //   G ⇔ 発電 / D ⇔ DR / L ⇔ 小売 or 自己託送。
  const prevIsOpenRef = useRef(false)
  const prevLicenseTypeRef = useRef<LicenseType | undefined>(undefined)
  useEffect(() => {
    if (isEdit || !isOpen) {
      prevIsOpenRef.current = isOpen
      prevLicenseTypeRef.current = licenseType
      return
    }
    const isOpenBecameTrue = !prevIsOpenRef.current
    const licenseTypeChanged = prevLicenseTypeRef.current !== licenseType
    prevIsOpenRef.current = isOpen
    prevLicenseTypeRef.current = licenseType
    if (!isOpenBecameTrue && !licenseTypeChanged) {
      return
    }
    const prefix = REQUIRED_BG_CODE_PREFIX[licenseType]
    if (prefix === undefined) {
      return
    }
    const current = watch('bgCode') ?? ''
    const knownPrefixPattern = /^[GLD]/i
    if (current === '') {
      setValue('bgCode', prefix, {
        shouldDirty: false,
        shouldValidate: false,
      })
      return
    }
    if (knownPrefixPattern.test(current[0])) {
      const next = prefix + current.slice(1)
      if (next !== current) {
        setValue('bgCode', next, {
          shouldDirty: false,
          shouldValidate: false,
        })
      }
    }
  }, [isEdit, isOpen, licenseType, setValue, watch])

  // AC-OMB-019: 新規作成で BG区分 を発電以外から発電に切り替えたとき、
  // 発電ライセンスを持たない代表・構成事業者が選択済みのまま残ると
  // 送信時に backend のライセンス検証で InvalidArgument になる。
  // 区分変更時に選択肢外の事業者をクリアし、再選択を促す。
  const isInitialMemberClear = useRef(true)
  const prevIsGenerationRef = useRef(false)
  useEffect(() => {
    if (isInitialMemberClear.current) {
      isInitialMemberClear.current = false
      prevIsGenerationRef.current = isGeneration
      return
    }
    if (isEdit || !isOpen) {
      prevIsGenerationRef.current = isGeneration
      return
    }
    const becameGeneration = !prevIsGenerationRef.current && isGeneration
    prevIsGenerationRef.current = isGeneration
    if (!becameGeneration || businessMasters.length === 0) {
      return
    }

    const validIds = new Set(filteredBusinessMasters.map((bm) => bm.id))
    const currentRep =
      (watch('repBusinessMasterId') as string | undefined) ?? ''
    const currentMembers = watch('members') ?? []

    const repValid = currentRep === '' || validIds.has(currentRep)
    const invalidMemberExists = currentMembers.some(
      (m) => m.businessMasterId !== '' && !validIds.has(m.businessMasterId),
    )

    if (repValid && !invalidMemberExists) {
      return
    }

    const emptyMember: BgMasterFormInput['members'][number] = {
      businessMasterId: '',
      isOwn: false,
      validFrom: todayCivilDate(),
      validTo: undefined,
    }

    // 代表が無効（発電ライセンスを持たない）場合は代表を空にする。
    // 構成事業者のうち、発電ライセンスを持つ事業者・空行はそのまま保持する。
    const repMember = repValid
      ? (currentMembers.find((m) => m.businessMasterId === currentRep) ?? {
          ...emptyMember,
          businessMasterId: currentRep,
        })
      : emptyMember

    if (!repValid) {
      setValue('repBusinessMasterId', '', { shouldDirty: true })
    }

    const nextMembers = [
      repMember,
      ...currentMembers.filter(
        (m) =>
          m.businessMasterId !== currentRep &&
          (m.businessMasterId === '' || validIds.has(m.businessMasterId)),
      ),
    ]
    setValue('members', nextMembers, { shouldDirty: true })
  }, [
    isEdit,
    isOpen,
    isGeneration,
    businessMasters,
    filteredBusinessMasters,
    setValue,
    watch,
  ])

  return (
    <Stack gap={4}>
      {/* 適用期間が終了している既存レコードを編集しているときの案内帯。
          新規作成では出さない (「終了している既存データ」を指す文言のため)。 */}
      {isEdit && (
        <ExpiredMasterAlert
          control={control as unknown as Control<FieldValues>}
          idPrefix="bgmaster"
        />
      )}

      {/* licenseType + bgCode: 2カラム (design: BG区分=左/BGコード=右)。
          BG区分を先に置くのは design HTML #3732 (2026-07-27) の意図に合わせるため
          (区分を先に決めさせて入力誤りを減らす)。 */}
      <SimpleGrid gap={3} columns={2}>
        {/* licenseType。 単一 select の正は DS `Select`
            (`~/components/common/FormSelect` の JSDoc 参照)。 */}
        <FieldRoot
          alignItems="stretch"
          invalid={'licenseType' in errors && errors.licenseType !== undefined}
          required
        >
          {/* readonly Input のときだけ明示 id で紐付ける。 DS `Select` は
              FieldRoot context の id を自動で使うため htmlFor を渡さない
              (渡すと存在しない id を指す label になる)。 */}
          <FieldLabel htmlFor={isEdit ? 'bgmaster-licenseType' : undefined}>
            {t('page:adminOcctoMaster.bgMaster.form.licenseType')}
            <FieldRequiredIndicator />
          </FieldLabel>
          {isEdit ? (
            <ImmutableInput
              id="bgmaster-licenseType"
              readOnly
              value={licenseTypeLabel(licenseType)}
            />
          ) : (
            <FormSelect
              aria-label={t('page:adminOcctoMaster.bgMaster.form.licenseType')}
              control={control}
              fallbackLabel={(value) => licenseTypeLabel(Number(value))}
              name="licenseType"
              numeric
              options={licenseTypeOptions}
            />
          )}
          {'licenseType' in errors && errors.licenseType !== undefined && (
            <FieldErrorText>
              {
                (errors as Record<string, { message?: string }>)['licenseType']
                  ?.message
              }
            </FieldErrorText>
          )}
        </FieldRoot>

        {/* bgCode */}
        <Controller
          control={control}
          name="bgCode"
          render={({ field }) => (
            <FieldRoot
              alignItems="stretch"
              invalid={errors.bgCode !== undefined}
              required
            >
              <FieldLabel htmlFor="bgmaster-bgCode">
                {t('page:adminOcctoMaster.bgMaster.form.bgCode')}
                <FieldRequiredIndicator />
              </FieldLabel>
              <ImmutableInput
                {...field}
                id="bgmaster-bgCode"
                maxLength={5}
                placeholder={t(
                  'page:adminOcctoMaster.bgMaster.form.bgCodePlaceholder',
                )}
                readOnly={isEdit}
              />
              {errors.bgCode !== undefined && (
                <FieldErrorText>{errors.bgCode.message}</FieldErrorText>
              )}
            </FieldRoot>
          )}
        />
      </SimpleGrid>

      {/* bgName + areaCode: 2カラム (design: BG名称=左/エリア=右) */}
      <SimpleGrid gap={3} columns={2}>
        {/* bgName */}
        <Controller
          control={control}
          name="bgName"
          render={({ field }) => (
            <FieldRoot
              alignItems="stretch"
              invalid={errors.bgName !== undefined}
              required
            >
              <FieldLabel htmlFor="bgmaster-bgName">
                {t('page:adminOcctoMaster.bgMaster.form.bgName')}
                <FieldRequiredIndicator />
              </FieldLabel>
              {/* HTML `maxLength` は UTF-16 code unit 基準で切るため、 rune 数
                  上限 (Zod `withinRunes(30)` / proto CEL / backend の 30) と
                  乖離する (𠮷 U+20BB7 等の補助 plane 文字は 1 rune = 2
                  UTF-16 units で、 15 rune 時点で入力不能になる)。 design HTML
                  #bgm-name の maxlength="25" は schema と drift しているため
                  採用せず、 文字数上限は Zod に一元化する。 */}
              {/* 例示値は domain の SoT である mock-app seed の実サンプル
                  (`OcctoMasterSeed.cs` の BgName = "東京発電BG") を採る。
                  maxLength は spec `bg.md:22` の string(30) / schema の
                  withinRunes(30) に揃える (8080 プロトは maxlength="25" だが、
                  spec が 30 なのでそちらが正)。 */}
              <Input
                {...field}
                id="bgmaster-bgName"
                maxLength={30}
                placeholder={t(
                  'page:adminOcctoMaster.bgMaster.form.bgNamePlaceholder',
                )}
              />
              {errors.bgName !== undefined && (
                <FieldErrorText>{errors.bgName.message}</FieldErrorText>
              )}
            </FieldRoot>
          )}
        />

        {/* areaCode */}
        <FieldRoot
          alignItems="stretch"
          invalid={'areaCode' in errors && errors.areaCode !== undefined}
          required
        >
          <FieldLabel htmlFor={isEdit ? 'bgmaster-areaCode' : undefined}>
            {t('page:adminOcctoMaster.bgMaster.form.areaCode')}
            <FieldRequiredIndicator />
          </FieldLabel>
          {isEdit ? (
            <ImmutableInput
              id="bgmaster-areaCode"
              readOnly
              value={convertAreaLabel(areaCode)}
            />
          ) : (
            <FormSelect
              aria-label={t('page:adminOcctoMaster.bgMaster.form.areaCode')}
              control={control}
              name="areaCode"
              numeric
              options={areaCodeOptions}
            />
          )}
          {'areaCode' in errors && errors.areaCode !== undefined && (
            <FieldErrorText>
              {
                (errors as Record<string, { message?: string }>)['areaCode']
                  ?.message
              }
            </FieldErrorText>
          )}
        </FieldRoot>
      </SimpleGrid>

      {/*
       * 電源種別 (発電 BG の時だけ・導出値の文) + repBusinessMasterId: 2 カラム。
       */}
      <SimpleGrid gap={3} columns={2}>
        {/* 電源種別: 発電 BG の時だけ、所属する発電所から決まる値を文で出す (入力欄にしない) */}
        {isGeneration && (
          <FieldRoot>
            <FieldLabel>
              {t('page:adminOcctoMaster.bgMaster.form.powerType')}
            </FieldLabel>
            <Text
              textStyle="body"
              data-testid="bgmaster-powerType"
              title={t('page:adminOcctoMaster.bgMaster.form.powerTypeHover')}
            >
              {derivedPowerTypeLabel(powerType)}
            </Text>
          </FieldRoot>
        )}

        {/* repBusinessMasterId。 members[0] への同期は OwnerMembersSection が
            repBusinessMasterId の変化を監視して行う (= 同期ロジックは
            PlantMaster と共通化) ため、 ここでは副作用を持たない。 */}
        <FieldRoot
          alignItems="stretch"
          invalid={errors.repBusinessMasterId !== undefined}
          required
        >
          <FieldLabel>
            {t('page:adminOcctoMaster.bgMaster.form.repBusinessMaster')}
            <FieldRequiredIndicator />
          </FieldLabel>
          <FormSelect
            aria-label={t(
              'page:adminOcctoMaster.bgMaster.form.repBusinessMaster',
            )}
            control={control}
            fallbackLabel={(id) => businessMasterFallbackLabel(id, targetDate)}
            name="repBusinessMasterId"
            options={businessMasterOptions}
            placeholder={t(
              'page:adminOcctoMaster.bgMaster.form.repBusinessMasterUnselected',
            )}
          />
          {errors.repBusinessMasterId !== undefined && (
            <FieldErrorText>
              {errors.repBusinessMasterId.message}
            </FieldErrorText>
          )}
        </FieldRoot>
      </SimpleGrid>

      {/* contractIdNo1 + wheelingLinkCode: 2カラム (design: 契約識別番号1/電源特定コード)。
          - contractIdNo1: BG区分=発電 以外では **grayed-out** (非表示にはしない)。
            非表示にすると grid の右列 (電源特定コード) が単独で残って幅が乱れる &
            ユーザーが「そこにある項目が発電時のみ入力可」と気付けない。 disabled で
            残すことで design HTML の grid レイアウトと同じ配置を維持する。
          - wheelingLinkCode: 自己託送 BG のみ入力可 (spec bg.md L25/L36)。
          - FieldRoot は `alignItems="stretch"` を付けて Input が列幅一杯に広がるように
            する (無いと chakra Field の default alignment で Input が縮み、
            BG名称/エリア と横幅が揃わない)。 */}
      <SimpleGrid gap={3} columns={2}>
        <Controller
          control={control}
          name="contractIdNo1"
          render={({ field }) => (
            <FieldRoot
              alignItems="stretch"
              disabled={!isGeneration}
              invalid={
                'contractIdNo1' in errors && errors.contractIdNo1 !== undefined
              }
              required={isGeneration}
            >
              {/* ⓘ tooltip は FieldLabel の外 (兄弟) に置く。
                  <label> の中に aria-label 付きの Box を入れると getByRole の
                  accessible name が「契約識別番号 1 補足情報」に膨らみ、
                  label 一致で拾う F5 test (`{ name: '契約識別番号 1' }`) が落ちる。
                  HStack で横並びに置くことで label の accessible name を保ったまま
                  design HTML のラベル横 ⓘ レイアウトを再現する。 */}
              <HStack gap={1}>
                <FieldLabel htmlFor="bgmaster-contractIdNo1">
                  {t('page:adminOcctoMaster.bgMaster.form.contractIdNo1')}
                  {isGeneration && <FieldRequiredIndicator />}
                </FieldLabel>
                <HelpIcon
                  ariaLabel={t(
                    'page:adminOcctoMaster.bgMaster.form.contractIdNo1TooltipAriaLabel',
                  )}
                  content={t(
                    'page:adminOcctoMaster.bgMaster.form.contractIdNo1Tooltip',
                  )}
                />
              </HStack>
              {/* placeholder は入力できる時だけ出す (BG区分≠発電 では非表示にせず
                  disabled で残す設計なので、 無条件だとグレーアウトした欄が入力例を
                  広告する)。 同ファイルの wheelingLinkCode と同じ扱い。
                  例示値は mock-app seed の実サンプル (`OcctoMasterSeed.cs` の
                  ContractIdNo1 = "CT-001") を採る。 8080 プロトは `例：JP06182` を
                  置いているが、 **JP06182 は「契約識別番号2」 のデータ項目 ID**
                  (`docs/occto/occto-power-sales-plan-spec.md:271`) であって値の例では
                  なく、 プロトが 2 番の placeholder を 1 番へ流用した誤り。
                  design HTML の sample data は
                  domain fact として採らない。
                  maxLength は spec `bg.md:27` の string(20) / schema の
                  withinRunes(20) に揃える。 */}
              <Input
                {...field}
                disabled={!isGeneration}
                id="bgmaster-contractIdNo1"
                maxLength={20}
                placeholder={
                  isGeneration
                    ? t(
                        'page:adminOcctoMaster.bgMaster.form.contractIdNo1Placeholder',
                      )
                    : ''
                }
                value={field.value ?? ''}
              />
              {'contractIdNo1' in errors &&
                errors.contractIdNo1 !== undefined && (
                  <FieldErrorText>
                    {
                      (errors as Record<string, { message?: string }>)[
                        'contractIdNo1'
                      ]?.message
                    }
                  </FieldErrorText>
                )}
            </FieldRoot>
          )}
        />

        {/* wheelingLinkCode (自己託送 BG のみ入力可。spec bg.md L25, L36) */}
        <Controller
          control={control}
          name="wheelingLinkCode"
          render={({ field }) => (
            <FieldRoot
              alignItems="stretch"
              invalid={
                'wheelingLinkCode' in errors &&
                errors.wheelingLinkCode !== undefined
              }
            >
              <FieldLabel htmlFor="bgmaster-wheelingLinkCode">
                {t('page:adminOcctoMaster.bgMaster.form.wheelingLinkCode')}
              </FieldLabel>
              <Input
                {...field}
                disabled={!isSelfWheeling}
                id="bgmaster-wheelingLinkCode"
                maxLength={5}
                onChange={(e) => {
                  field.onChange(
                    e.target.value === '' ? undefined : e.target.value,
                  )
                }}
                placeholder={
                  isSelfWheeling
                    ? t(
                        'page:adminOcctoMaster.bgMaster.form.wheelingLinkCodePlaceholder',
                      )
                    : ''
                }
                value={field.value ?? ''}
              />
              {'wheelingLinkCode' in errors &&
                errors.wheelingLinkCode !== undefined && (
                  <FieldErrorText>
                    {
                      (errors as Record<string, { message?: string }>)[
                        'wheelingLinkCode'
                      ]?.message
                    }
                  </FieldErrorText>
                )}
            </FieldRoot>
          )}
        />
      </SimpleGrid>

      {/* validFrom / validTo (design #bgm-valid-from 相当。 旧 contractStartDate は
          occto-master-valid-period-6tabs 設計書で validFrom に統合・廃止された
          (AC-10)。 適用開始日は編集可能 (design §4 主要意思決定)。 有効期間
          フィールド構造〈BG レベル単一期間 vs 構成事業者行ごとの
          ValidFrom/ValidTo〉自体は spec bg.md 準拠で意図的に変更済みのため
          touch しない。 design のドキュメント順序 (契約識別番号1 の直後、
          備考の前) にのみ合わせる。 共通 TopLevelValidPeriodFields で描画。 */}
      <TopLevelValidPeriodFields
        control={control as unknown as Control<FieldValues>}
        idPrefix="bgmaster"
        validFromError={
          'validFrom' in errors
            ? (errors as Record<string, { message?: string }>)['validFrom']
                ?.message
            : undefined
        }
        validToError={
          'validTo' in errors
            ? (errors as Record<string, { message?: string }>)['validTo']
                ?.message
            : undefined
        }
      />

      {/* note (design #bgm-note、 備考は有効期間の直後・構成事業者の前)。
          上限は guidelines/input-character-limits.md の説明・メモ系既定値 100 文字
          (入力制約は design HTML の maxlength から採らない)。 HTML `maxLength` は UTF-16
          code unit で切り、 補助 plane の文字を含む 100 rune を入力できないため付けず、
          上限は schema の withinRunes(100) に一元化する。 hint の見た目は design/CLAUDE.md
          §「ヒント文言は .form-hint (weight 400 / sm / mid-emphasis)」 に従い、 Text as="span" + textStyle="captionNormal" + Text.midEmphasis で描画する。 */}
      <Controller
        control={control}
        name="note"
        render={({ field }) => (
          <FieldRoot invalid={errors.note !== undefined}>
            <FieldLabel>
              {t('page:adminOcctoMaster.bgMaster.form.note')}
            </FieldLabel>
            <Textarea {...field} value={field.value ?? ''} />
            {errors.note !== undefined && (
              <FieldErrorText>{errors.note.message}</FieldErrorText>
            )}
            <Text
              as="span"
              textStyle="captionNormal"
              marginTop={1}
              color="Text.midEmphasis"
            >
              {t('page:adminOcctoMaster.bgMaster.form.noteHint')}
            </Text>
          </FieldRoot>
        )}
      />

      {/*
       * 構成事業者 編集 UI (代表同期含む。 PlantMaster と共通)。
       * design (`design/admin/occto.html` #bgm-members, 行810-814) はフォーム
       * 最下部 (備考の後、 配下系統コード〈スコープ外〉の前) に配置。 従来は
       * 代表事業者の直後に置いていたが、 design parity 再検証で順序ズレと
       * 判明したため最下部へ移動した (2026-07-10)。
       * showValidityPeriod で各行に ValidFrom / ValidTo 入力欄を表示する
       * (AC-02 / AC-03。 PlantMaster 側は plant 自体の validFrom を全 member に
       * 適用する既存パターンのため省略、 デフォルト false のまま渡さない)。
       */}
      <OwnerMembersSection
        addDisabledHint={
          isGeneration
            ? t('page:adminOcctoMaster.bgMaster.form.membersGenerationHint')
            : isSelfWheeling
              ? t('page:adminOcctoMaster.bgMaster.form.membersSelfWheelingHint')
              : undefined
        }
        businessMasterFallbackLabel={businessMasterFallbackLabel}
        businessMasters={filteredBusinessMasters}
        canAdd={!isGeneration && !isSelfWheeling}
        control={control as unknown as Control<FieldValues>}
        i18nNs="adminOcctoMaster.bgMaster"
        recordValidFrom={validFrom}
        repFieldName="repBusinessMasterId"
        setValue={setValue as unknown as UseFormSetValue<FieldValues>}
        // 発電 BG・自己託送 BG の所属事業者は代表 1者だけ (Q10)
        showBgDecorations
        showValidityPeriod
      />
    </Stack>
  )
}
